// packages/common/src/sse/useRosterSSE.ts
import { useCallback, useRef } from 'react';
import { useSSE } from './useSSE';

export type RosterEventName =
  | 'creator_accepted_by_brand'
  | 'creator_accepted_by_admin'
  | 'creator_joined'
  | 'creator_rejected'
  | 'creator_reinstated'
  | 'post_held'
  | 'post_hold_released'
  | 'post_approval_rolled_back'
  | 'creator_acceptance_rolled_back'
  | 'creator_brand_approval_rolled_back'
  | 'script_uploaded'
  | 'script_approved'
  | 'content_uploaded'
  | 'content_approved'
  | 'campaign_creator_went_live'
  | 'campaign_kpis_updated'
  | 'campaign_live_views_trend_updated'
  | 'campaign_live_stats_updated'
  | 'import.complete'
  | 'import.progress';

export type RosterEventPayload = {
  campaign_id?: string;
  entry_id?: string;
};

interface Options {
  enabled: boolean;
  onEvent: (eventType: RosterEventName, payload: RosterEventPayload) => void;
}

export function useRosterSSE({ enabled, onEvent }: Options): void {
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const onAcceptedByBrand   = useCallback((p: Record<string, unknown>) => onEventRef.current('creator_accepted_by_brand',         p as RosterEventPayload), []);
  const onAcceptedByAdmin   = useCallback((p: Record<string, unknown>) => onEventRef.current('creator_accepted_by_admin',         p as RosterEventPayload), []);
  const onCreatorJoined     = useCallback((p: Record<string, unknown>) => onEventRef.current('creator_joined',                    p as RosterEventPayload), []);
  const onCreatorRejected   = useCallback((p: Record<string, unknown>) => onEventRef.current('creator_rejected',                  p as RosterEventPayload), []);
  const onCreatorReinstated = useCallback((p: Record<string, unknown>) => onEventRef.current('creator_reinstated',                p as RosterEventPayload), []);
  const onPostHeld          = useCallback((p: Record<string, unknown>) => onEventRef.current('post_held',                         p as RosterEventPayload), []);
  const onPostHoldReleased  = useCallback((p: Record<string, unknown>) => onEventRef.current('post_hold_released',                p as RosterEventPayload), []);
  const onApprovalRolledBack = useCallback((p: Record<string, unknown>) => onEventRef.current('post_approval_rolled_back',        p as RosterEventPayload), []);
  const onAcceptanceRolledBack = useCallback((p: Record<string, unknown>) => onEventRef.current('creator_acceptance_rolled_back', p as RosterEventPayload), []);
  const onBrandApprovalRolledBack = useCallback((p: Record<string, unknown>) => onEventRef.current('creator_brand_approval_rolled_back', p as RosterEventPayload), []);
  const onScriptUploaded    = useCallback((p: Record<string, unknown>) => onEventRef.current('script_uploaded',                   p as RosterEventPayload), []);
  const onScriptApproved    = useCallback((p: Record<string, unknown>) => onEventRef.current('script_approved',                   p as RosterEventPayload), []);
  const onContentUploaded   = useCallback((p: Record<string, unknown>) => onEventRef.current('content_uploaded',                  p as RosterEventPayload), []);
  const onContentApproved   = useCallback((p: Record<string, unknown>) => onEventRef.current('content_approved',                  p as RosterEventPayload), []);
  const onWentLive          = useCallback((p: Record<string, unknown>) => onEventRef.current('campaign_creator_went_live',         p as RosterEventPayload), []);
  const onKPIsUpdated       = useCallback((p: Record<string, unknown>) => onEventRef.current('campaign_kpis_updated',             p as RosterEventPayload), []);
  const onTrendUpdated      = useCallback((p: Record<string, unknown>) => onEventRef.current('campaign_live_views_trend_updated',  p as RosterEventPayload), []);
  const onLiveStatsUpdated  = useCallback((p: Record<string, unknown>) => onEventRef.current('campaign_live_stats_updated',       p as RosterEventPayload), []);
  const onImportComplete    = useCallback((p: Record<string, unknown>) => onEventRef.current('import.complete',                   p as RosterEventPayload), []);
  const onImportProgress    = useCallback((p: Record<string, unknown>) => onEventRef.current('import.progress',                   p as RosterEventPayload), []);

  useSSE('creator_accepted_by_brand',            enabled, onAcceptedByBrand);
  useSSE('creator_accepted_by_admin',            enabled, onAcceptedByAdmin);
  useSSE('creator_joined',                        enabled, onCreatorJoined);
  useSSE('creator_rejected',                     enabled, onCreatorRejected);
  useSSE('creator_reinstated',                   enabled, onCreatorReinstated);
  useSSE('post_held',                            enabled, onPostHeld);
  useSSE('post_hold_released',                   enabled, onPostHoldReleased);
  useSSE('post_approval_rolled_back',            enabled, onApprovalRolledBack);
  useSSE('creator_acceptance_rolled_back',       enabled, onAcceptanceRolledBack);
  useSSE('creator_brand_approval_rolled_back',   enabled, onBrandApprovalRolledBack);
  useSSE('script_uploaded',                      enabled, onScriptUploaded);
  useSSE('script_approved',                      enabled, onScriptApproved);
  useSSE('content_uploaded',                     enabled, onContentUploaded);
  useSSE('content_approved',                     enabled, onContentApproved);
  useSSE('campaign_creator_went_live',           enabled, onWentLive);
  useSSE('campaign_kpis_updated',                enabled, onKPIsUpdated);
  useSSE('campaign_live_views_trend_updated',    enabled, onTrendUpdated);
  useSSE('campaign_live_stats_updated',          enabled, onLiveStatsUpdated);
  useSSE('import.complete',                      enabled, onImportComplete);
  useSSE('import.progress',                      enabled, onImportProgress);
}
